import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject, signal } from '@angular/core';

export type ThemeMode = 'light' | 'dark' | 'system';

const THEME_MODES: readonly ThemeMode[] = ['light', 'dark', 'system'];

/**
 * Resolves and applies the active color theme. The user can pick `light`,
 * `dark`, or `system` (follow the OS preference). The choice is persisted to
 * localStorage and reflected as a `.light-theme` / `.dark-theme` class on
 * <body>, which is what the design tokens key off of.
 */
@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private static readonly STORAGE_KEY = 'medone.theme-mode';

  private readonly document = inject(DOCUMENT);
  private readonly mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

  /** Live OS-level dark preference; only affects the UI while in `system` mode. */
  private readonly systemPrefersDark = signal(this.mediaQuery.matches);

  /** The user's selected mode; defaults to `system` until they choose otherwise. */
  readonly mode = signal<ThemeMode>(this.readStoredMode());

  /** Whether dark styling is currently active, given the mode and OS setting. */
  readonly isDark = computed(() =>
    this.mode() === 'system' ? this.systemPrefersDark() : this.mode() === 'dark',
  );

  constructor() {
    this.mediaQuery.addEventListener('change', (event) => {
      this.systemPrefersDark.set(event.matches);
      this.applyThemeClass();
    });

    this.applyThemeClass();
  }

  setMode(mode: ThemeMode): void {
    this.mode.set(mode);
    this.persistMode(mode);
    this.applyThemeClass();
  }

  private applyThemeClass(): void {
    const classList = this.document.body.classList;
    classList.remove('light-theme', 'dark-theme');
    classList.add(this.isDark() ? 'dark-theme' : 'light-theme');
  }

  private readStoredMode(): ThemeMode {
    const stored = this.safeRead();
    return THEME_MODES.includes(stored as ThemeMode) ? (stored as ThemeMode) : 'system';
  }

  private persistMode(mode: ThemeMode): void {
    try {
      localStorage.setItem(ThemeService.STORAGE_KEY, mode);
    } catch {
      // Storage can be unavailable (private mode, blocked cookies); the theme
      // still applies for the session, it just won't be remembered.
    }
  }

  private safeRead(): string | null {
    try {
      return localStorage.getItem(ThemeService.STORAGE_KEY);
    } catch {
      return null;
    }
  }
}
